import { DownOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Collapse,
  Dropdown,
  Flex,
  Input,
  type MenuProps,
  Slider,
  type SliderSingleProps,
  Space,
  message,
} from "antd";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { NoteType, isCustomisedTemplate } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { getCustomNote, getCustomizationList, handleWriteNoteSubmission } from "../../service/notes.service";
import {
  setAITemplateModalState,
  setCustomiseSectionState,
  setNotesViewVisible,
  setShowDemoSignupModal,
  setShowRestrictionModal,
  setStoredTemplateData,
} from "../../slices/appStateSlice";
import { clearSuggestion, setCustomizationList, setNoteTypeInstructionNote } from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import { getNoteContentAsText } from "../../utils/datamanipulation.utils";
import styles from "./customise-note.module.scss";
import { combineInstructions, getSavedInstructionsForNoteType } from "./customise-note.utils";

const formatter: NonNullable<SliderSingleProps["tooltip"]>["formatter"] = (value) => {
  if (value === 0) {
    return "Low";
  }
  if (value === 1) {
    return "Default";
  }
  return "High";
};

const formatterFormatting: NonNullable<SliderSingleProps["tooltip"]>["formatter"] = (value) => {
  if (value === 0) {
    return "Bullet";
  }
  if (value === 1) {
    return "Paragraph";
  }
  return "Numbered";
};

type TProps = {
  noPadding?: boolean;
};

const CustomiseNote: React.FC<TProps> = ({ noPadding }: TProps) => {
  const submitButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const componentName = "customise-note";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const [settings, setSettings] = React.useState({
    note_section: "all",
    detail_level: 1,
    formatting: 1,
    is_system: false,
  });
  const { notesDetails } = useAppSelector((state) => state.notes);
  const { clients } = useAppSelector((state) => state.client);
  const { isDemoPath, isMobilePath, isCustomiseSectionVisible } = useAppSelector((state) => state.appState);
  const { customizationList, suggestion, note_type_instructions } = useAppSelector((state) => state.client);
  const [instructions, setInstructions] = React.useState("");
  const textareaRef = React.useRef<any>(null);

  const isCustomTemplateNote = useMemo(
    () => isCustomisedTemplate(notesDetails?.note_type_info?.pk as number, notesDetails?.note_type as number),
    [notesDetails],
  );

  // Clear suggestion when drawer closes
  React.useEffect(() => {
    if (!isCustomiseSectionVisible) {
      dispatch(clearSuggestion());
      setInstructions("");
    }
  }, [isCustomiseSectionVisible, dispatch]);

  const SOAP_ITEMS = [
    {
      key: "1",
      value: "all",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "all" }));
          }}
        >
          Entire Note
        </div>
      ),
    },
    {
      key: "2",
      value: "subjective",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "subjective" }));
          }}
        >
          Subjective Only
        </div>
      ),
    },
    {
      key: "3",
      value: "objective",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "objective" }));
          }}
        >
          Objective Only
        </div>
      ),
    },
    {
      key: "4",
      value: "assessment",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "assessment" }));
          }}
        >
          Assessment Only
        </div>
      ),
    },
    {
      key: "5",
      value: "plan",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "plan" }));
          }}
        >
          Plan Only
        </div>
      ),
    },
  ];

  const DAP_ITEM = [
    {
      key: "1",
      value: "all",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "all" }));
          }}
        >
          Entire Note
        </div>
      ),
    },
    {
      key: "2",
      value: "data",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "data" }));
          }}
        >
          Data Only
        </div>
      ),
    },
    {
      key: "3",
      value: "assessment",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "assessment" }));
          }}
        >
          Assessment Only
        </div>
      ),
    },
    {
      key: "4",
      value: "plan",
      label: (
        <div
          onClick={() => {
            setSettings((prev) => ({ ...prev, note_section: "plan" }));
          }}
        >
          Plan Only
        </div>
      ),
    },
  ];

  const customizationInstructions = [
    "Write short instructions to customize parts of the note",
    "Do not copy/paste an entirely new template",
    "Try using direct instructions instead of if/else statements",
  ];

  const items: MenuProps["items"] = notesDetails?.note_type === NoteType.SOAP ? SOAP_ITEMS : DAP_ITEM;

  const marks: SliderSingleProps["marks"] = {
    0: "Low",
    1: "Default",
    2: "High",
  };
  const marksFormatting: SliderSingleProps["marks"] = {
    0: "Bullet",
    1: "Paragraph",
    2: "Numbered",
  };

  const customize = () => {
    if (isDemoPath) {
      dispatch(setShowDemoSignupModal(true));
      dispatch(setCustomiseSectionState(false));
      return;
    }
    if (isRegenerating) return;
    if (notesDetails) {
      setIsRegenerating(true);
      const clientUuid = clients.find((c) => c.id === notesDetails.client)?.uuid;
      handleWriteNoteSubmission({
        audio_id: notesDetails?.id,
        title: notesDetails.title,
        note_type: notesDetails.note_type,
        category_type: notesDetails.category_type,
        gender_type: notesDetails.gender_type,
        client_id: notesDetails.client,
        location_type: notesDetails.location_type,
        duration_type: notesDetails.duration_type,
        duration_addon_type: notesDetails.duration_addon_type,
        customization: {
          customization_type:
            notesDetails?.note_type === NoteType.SOAP ? 0 : notesDetails?.note_type === NoteType.DAP ? 1 : 2,
          note_type_instructions: {
            ...note_type_instructions,
            [notesDetails?.note_type]: {
              note: instructions,
              is_system:
                notesDetails?.note_type === NoteType.SOAP || notesDetails?.note_type === NoteType.DAP
                  ? settings.is_system
                  : false,
            },
          },
          is_system:
            notesDetails?.note_type === NoteType.SOAP || notesDetails?.note_type === NoteType.DAP
              ? settings.is_system
              : false,
          note_section: settings.note_section,
          detail_level: settings.detail_level,
          formatting: settings.formatting,
        },
      })
        .then(() => {
          const rootPath = isMobilePath ? "/notesforMobile" : "/home";
          if (clientUuid) {
            navigate(`${rootPath}/clients/${clientUuid}`);
          } else {
            navigate(rootPath);
          }
          dispatch(setCustomiseSectionState(false));
          dispatch(setNotesViewVisible(false));
          dispatch(setNotesDetails(null));
          dispatch(setCustomiseSectionState(false));
          dispatch(resetNotesDetails());
          dispatch(clearSuggestion());
          getCustomizationList()
            .then((response) => {
              dispatch(setCustomizationList(response.data));
            })
            .catch((error) => {
              console.error("Error fetching customization list:", error);
            });
          message.success("Regenerating note with customizations");
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsRegenerating(false);
        });
    }
  };

  const getSelection = () => {
    const options = notesDetails?.note_type === NoteType.SOAP ? SOAP_ITEMS : DAP_ITEM;
    return <>{options.find((item) => item.value === settings.note_section)?.label || ""}</>;
  };

  const restoreExistingSettings = useCallback(() => {
    if (customizationList?.length) {
      const customizationType =
        notesDetails?.note_type === NoteType.SOAP ? 0 : notesDetails?.note_type === NoteType.DAP ? 1 : 2;
      const existingCustomization = customizationList.find((item) => item.customization_type === customizationType);
      if (existingCustomization) {
        setSettings({
          note_section: existingCustomization.customization.note_section,
          detail_level: existingCustomization.customization.detail_level,
          formatting: existingCustomization.customization.formatting,
          is_system: existingCustomization.customization.is_system || false,
        });
        if (existingCustomization.customization.note_type_instructions && notesDetails && notesDetails.note_type) {
          dispatch(setNoteTypeInstructionNote(existingCustomization.customization.note_type_instructions));
        }
      }
    }
  }, [customizationList, notesDetails, dispatch]);

  // Add effect to initialize instructions from suggestion only (not template instructions)
  React.useEffect(() => {
    if (suggestion) {
      setInstructions(suggestion);
      // Focus the textarea after setting instructions
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [suggestion]);

  React.useEffect(() => {
    restoreExistingSettings();
  }, [restoreExistingSettings]);

  const openCreateTemplatePage = async () => {
    const loading = message.loading("Loading template...");
    dispatch(setCustomiseSectionState(false));
    if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
      loading();
      return;
    }
    if (!notesDetails || !notesDetails.note_type_info) return;

    if (notesDetails.note_type_info.pk) {
      // Custom template - fetch and open Create view
      const template = await getCustomNote(notesDetails.note_type_info.pk as number);
      loading();
      dispatch(
        setStoredTemplateData({
          pk: notesDetails.note_type_info.pk,
          ...template.data,
          document: template.data.document?.replace(/\\n/g, "\n"),
          appendInstructions: instructions,
        }),
      );
    } else {
      loading();
      // Premade template - open Create view with generated note as sample document
      const noteContent = getNoteContentAsText(notesDetails);

      // For non-SOAP/DAP prebuilt templates, look up the user's TYPE_ANY (2)
      // Customization object and pull saved is_system instructions to prefill the prompt box
      const savedInstructions = getSavedInstructionsForNoteType(customizationList, notesDetails.note_type);
      const combinedInstructions = combineInstructions(savedInstructions, instructions);

      dispatch(
        setStoredTemplateData({
          name: notesDetails.note_type_info.name,
          document: noteContent,
          appendInstructions: combinedInstructions,
          isFromTemplate: true,
        }),
      );
    }
    dispatch(setAITemplateModalState(true));
  };

  if (!notesDetails) {
    return null;
  }

  return (
    <Flex
      vertical
      gap={20}
      style={{
        padding: noPadding ? "0px" : "20px",
        height: notesDetails.note_type === 0 || notesDetails.note_type === 2 ? "auto" : "100%",
      }}
    >
      {(notesDetails.note_type === 0 || notesDetails.note_type === 2) && (
        <>
          <Flex>
            <Dropdown menu={{ items }}>
              <Button onClick={(e) => e.preventDefault()}>
                <Space>
                  Apply to <span>{getSelection()}</span>
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </Flex>
          <Flex vertical>
            <Flex vertical>
              <Collapse
                className={styles[`${componentName}__collapse`]}
                items={[
                  {
                    key: "1",
                    label: "Detail level",
                    extra: <b>{formatter(settings.detail_level)}</b>,
                    children: (
                      <Flex
                        vertical
                        style={{
                          padding: "0px 20px",
                        }}
                      >
                        <Slider
                          marks={marks}
                          min={0}
                          max={2}
                          value={settings.detail_level}
                          onChange={(value) =>
                            setSettings((prev) => ({
                              ...prev,
                              detail_level: value,
                            }))
                          }
                          tooltip={{ formatter }}
                          styles={{
                            track: {
                              backgroundColor: "#6366F1",
                            },
                          }}
                        />
                        {/* <Divider />
                      <Flex vertical>
                        <Flex gap={10}>
                          <QuestionCircleOutlined />
                          What's this
                        </Flex>
                        <Flex
                          style={{
                            marginLeft: "25px",
                          }}
                        >
                          Lorum Ipsum
                        </Flex>
                      </Flex> */}
                      </Flex>
                    ),
                  },
                ]}
              />
            </Flex>
          </Flex>
          <Flex>
            <Collapse
              className={styles[`${componentName}__collapse`]}
              items={[
                {
                  key: "1",
                  extra: <b>{formatterFormatting(settings.formatting)}</b>,
                  label: "Formatting style",
                  children: (
                    <Flex
                      vertical
                      style={{
                        padding: "0px 20px",
                      }}
                    >
                      <Slider
                        marks={marksFormatting}
                        value={settings.formatting}
                        onChange={(value) =>
                          setSettings((prev) => ({
                            ...prev,
                            formatting: value,
                          }))
                        }
                        min={0}
                        max={2}
                        tooltip={{ formatter: formatterFormatting }}
                        styles={{
                          track: {
                            backgroundColor: "#6366F1",
                          },
                        }}
                      />
                      {/* <Divider />
                    <Flex vertical>
                      <Flex gap={10}>
                        <QuestionCircleOutlined />
                        What's this
                      </Flex>
                      <Flex
                        style={{
                          marginLeft: "25px",
                        }}
                      >
                        Lorum Ipsum
                      </Flex>
                    </Flex> */}
                    </Flex>
                  ),
                },
              ]}
            />
          </Flex>
        </>
      )}
      {!(notesDetails.note_type === 0 || notesDetails.note_type === 2) && (
        <Flex
          vertical
          gap={5}
          style={{
            height: "100%",
          }}
        >
          <Flex vertical>
            <Flex>
              <ul
                style={{
                  listStylePosition: "inside",
                  paddingLeft: "0px",
                  marginTop: "6px",
                  marginBottom: "0px",
                }}
              >
                {customizationInstructions.map((item) => (
                  <li
                    key={item}
                    style={{
                      padding: "2px",
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </Flex>
            <Flex
              style={{
                fontSize: "14px",
                margin: "5px 0",
                textDecoration: "underline",
                cursor: "pointer",
                color: "var(--primary)",
              }}
              onClick={() => {
                window.open("https://getnudgeai.com/customize-note-faq", "_blank");
              }}
            >
              Learn more from examples
            </Flex>
          </Flex>
          <Input.TextArea
            ref={textareaRef}
            value={instructions}
            onChange={(e) => {
              setInstructions(e.target.value);
            }}
            style={{
              height: "100%",
            }}
            maxLength={1000}
            placeholder={`• Remove section on...
• Add a summary before section...
• Increase detail within....
• Use bullets points across...`}
          />
        </Flex>
      )}
      {notesDetails.note_type === NoteType.SOAP || notesDetails.note_type === NoteType.DAP ? (
        <>
          <Flex
            style={{
              backgroundColor: "var(--grey-bg)",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <Checkbox
              onChange={(e) => {
                setSettings((prev) => ({ ...prev, is_system: e.target.checked }));
                if (submitButtonRef?.current) {
                  submitButtonRef.current.innerText = e.target.checked ? "Save and Regenerate Note" : "Regenerate Note";
                }
              }}
              className={styles[`${componentName}__collapse-checkbox`]}
              checked={settings.is_system}
            >
              Save configuration for future {notesDetails.note_type === NoteType.SOAP ? "SOAP" : "DAP"} notes.
            </Checkbox>
          </Flex>
          <Flex>
            <Button
              className={styles[`${componentName}__collapse-save`]}
              type="primary"
              size="middle"
              onClick={customize}
              ref={submitButtonRef}
              loading={isRegenerating}
              disabled={isRegenerating}
            >
              Regenerate Note
            </Button>
          </Flex>
        </>
      ) : (
        <Flex gap={10} vertical align="center">
          <Button
            className={styles[`${componentName}__collapse-save`]}
            type="primary"
            size="middle"
            onClick={customize}
            ref={submitButtonRef}
            loading={isRegenerating}
            disabled={isRegenerating}
          >
            <span className={styles[`${componentName}__button-badge`]}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M1.16675 7.00001C1.16675 3.77826 3.77833 1.16668 7.00008 1.16668C9.51663 1.16668 11.661 2.76476 12.4731 5.00001M12.8334 7.00001C12.8334 10.2218 10.2218 12.8333 7.00008 12.8333C4.48354 12.8333 2.33913 11.2353 1.52701 9.00001M12.4731 5.00001V2.33334M12.4731 5.00001H9.80642M1.52701 9.00001V11.6667M1.52701 9.00001H4.19367"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              One-time
            </span>
            Regenerate Note
          </Button>
          <span className={styles[`${componentName}__divider`]}>or</span>
          <Button
            className={`${styles[`${componentName}__collapse-save`]} ${styles[`${componentName}__collapse-save--secondary`]}`}
            type="default"
            size="middle"
            onClick={openCreateTemplatePage}
          >
            <span
              className={`${styles[`${componentName}__button-badge`]} ${styles[`${componentName}__button-badge--secondary`]}`}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2.33333 0.5V3.16667M1 1.83333H3.66667M3 9.83333V12.5M1.66667 11.1667H4.33333M7.66667 0.5L9.19048 5.07143L13 6.5L9.19048 7.92857L7.66667 12.5L6.14286 7.92857L2.33333 6.5L6.14286 5.07143L7.66667 0.5Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Permanent
            </span>
            {isCustomTemplateNote ? "Add ChatGPT-Style Instructions" : "Create a custom template"}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default CustomiseNote;
